/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useState, useEffect, useCallback } from 'react'
import { Checkbox, Spin } from 'antd'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import Text from 'src/components/ts-components/Text'
import {
  fetchVideographers,
  Videographer,
} from 'src/pages/Game/VideoAssignment/api'
import req from '@sportninja/common/api/request'

interface VideographerSelectContentProps {
  gameId: string
  rosterId: string
  onInviteClick: () => void
  refreshKey?: number
}

const VideographerSelectContent: React.FC<VideographerSelectContentProps> = ({
  gameId,
  rosterId,
  onInviteClick,
  refreshKey,
}) => {
  const [videographers, setVideographers] = useState<Videographer[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set())

  const loadVideographers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchVideographers(gameId, rosterId)
      setVideographers(data)
    } catch {
      // Silently fail
    } finally {
      setLoading(false)
    }
  }, [gameId, rosterId])

  useEffect(() => {
    loadVideographers()
  }, [loadVideographers, refreshKey])

  const handleToggle = useCallback(
    async (videographer: Videographer, checked: boolean) => {
      const id = videographer.user_id || videographer.invitation_id
      if (!id) {
        return
      }

      setUpdatingIds((prev) => new Set(prev).add(id))
      try {
        await req(`/games/${gameId}/rosters/${rosterId}/users/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ is_attending: checked }),
        })
        setVideographers((prev) =>
          prev.map((v) => {
            const vId = v.user_id || v.invitation_id
            if (vId === id) {
              return { ...v, is_attending: checked } as Videographer & {
                is_attending: boolean
              }
            }
            return v
          })
        )
      } catch {
        // Silently fail
      } finally {
        setUpdatingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }
    },
    [gameId, rosterId]
  )

  if (loading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
        `}
      >
        <Spin />
      </div>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 300px;
          overflow-y: auto;
        `}
      >
        {videographers.length === 0 && !loading ? (
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_70}
          >
            No videographers assigned yet.
          </Text>
        ) : (
          videographers.map((v) => {
            const id = v.user_id || v.invitation_id || ''
            const isPending = !v.user_exists
            const isUpdating = updatingIds.has(id)
            const fullName =
              `${v.name_first} ${v.name_last}`.trim() || v.email || 'Unknown'
            const isAttending = (v as Videographer & { is_attending?: boolean })
              .is_attending

            return (
              <div
                key={id}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  padding: 12px;
                  background: ${colors.SECONDARY_400};
                  border: 1px solid ${colors.SECONDARY_100};
                  border-radius: 8px;
                  opacity: ${isPending ? 0.6 : 1};
                `}
              >
                <Checkbox
                  checked={isAttending ?? false}
                  disabled={isPending || isUpdating}
                  onChange={(e) => handleToggle(v, e.target.checked)}
                />
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                  `}
                >
                  <Text
                    variant='paragraphMedium'
                    weight='semiBold'
                    color={colors.WHITE}
                  >
                    {fullName}
                  </Text>
                  {isPending && (
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.WARNING_300}
                    >
                      Pending invitation
                    </Text>
                  )}
                  {v.email && !isPending && (
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.NEUTRAL_70}
                    >
                      {v.email}
                    </Text>
                  )}
                </div>
                {isUpdating && <Spin size='small' />}
              </div>
            )
          })
        )}
      </div>

      <button
        onClick={onInviteClick}
        css={css`
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 8px 0;
          text-align: left;
          color: ${isCanlan ? colors.CYAN_BLUE : colors.PRIMARY};
          font-size: 14px;
          &:hover {
            text-decoration: underline;
          }
        `}
      >
        Can&apos;t find who you&apos;re looking for? Invite a videographer
      </button>
    </div>
  )
}

export default VideographerSelectContent
